/**
 * Classify CSV company import failures for the review UI.
 * Identity conflicts are safe "needs review" outcomes, not system crashes.
 */

import type { ImportConflictRow } from '@/hooks/useCSVImport';

const IDENTITY_PATTERNS = [
  /already has a different website/i,
  /more than one company with this name/i,
  /similarly named company/i,
  /ambiguous company/i,
  /Review the company before/i,
  /Review it before creating/i,
];

/** Strip people_companies / store prefixes so identity patterns still match. */
function stripStorePrefix(message: string): string {
  return message.replace(/^(?:Company(?: store)?|Contact store):\s*/i, '').trim();
}

export function isIdentityConflictError(message: string): boolean {
  const cleaned = stripStorePrefix(message);
  return IDENTITY_PATTERNS.some((p) => p.test(cleaned));
}

export function parseImportErrorLine(line: string): { row: number; message: string } | null {
  // people_companies store prefixes: "Company: …" / "Company store: …"
  const match = line.match(/^Row\s+(\d+):\s*(?:Company(?: store)?:\s*)?(.+)$/i);
  if (!match) return null;
  const rowToken = match[1];
  const messageToken = match[2];
  if (rowToken === undefined || messageToken === undefined) return null;
  const row = Number(rowToken);
  if (!Number.isFinite(row) || row < 1) return null;
  return { row, message: messageToken.trim() };
}

function firstNonEmpty(...values: Array<string | undefined | null>): string {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function normalizeDomainish(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const withProto = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const host = new URL(withProto).hostname.replace(/^www\./i, '');
    return host || trimmed;
  } catch {
    return trimmed.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0] || trimmed;
  }
}

/**
 * Identity conflicts are about companies. For people+companies CSVs the person
 * lives in `name` and the employer in `company_name` — never treat the person
 * as the company for force-create or "open existing" lookup.
 */
/**
 * Optional trailing identity from people_companies company-phase errors:
 * `… [BaseOne · baseone.uk]`
 */
export function parseIdentityHintFromError(message: string): {
  companyName: string | null;
  domain: string | null;
} {
  const match = message.match(/\[([^\]]+)\]\s*$/);
  if (!match?.[1]) return { companyName: null, domain: null };
  const parts = match[1].split('·').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return { companyName: null, domain: null };
  if (parts.length === 1) {
    const only = parts[0]!;
    if (only.includes('.') && !only.includes(' ')) {
      return { companyName: null, domain: normalizeDomainish(only) };
    }
    return { companyName: only, domain: null };
  }
  return {
    companyName: parts[0] || null,
    domain: normalizeDomainish(parts[1]!) || parts[1] || null,
  };
}

export function extractConflictIdentity(
  data: Record<string, string>,
  row: number,
  errorMessage?: string,
): {
  companyName: string;
  personName: string | null;
  domain: string | null;
  /** Stable label for list rows (company when known). */
  displayName: string;
} {
  const hint = errorMessage
    ? parseIdentityHintFromError(errorMessage)
    : { companyName: null, domain: null };

  // Explicit company field wins (people + companies one-file imports).
  const explicitCompany = firstNonEmpty(
    hint.companyName,
    data.company_name,
    data.Company,
    data.company,
    data['company.name'],
    data['Company Name'],
  );
  const nameField = firstNonEmpty(
    data.name,
    data.Name,
    data.full_name,
    data['Full Name'],
    [data.first_name, data.last_name].filter(Boolean).join(' '),
    [data.First, data.Last].filter(Boolean).join(' '),
  );

  // Company-only CSV maps company → `name`. People CSV maps person → `name`
  // and employer → `company_name`. Never force-create a company named after a person.
  const resolvedCompany = explicitCompany || nameField || `Row ${row}`;
  const personName =
    explicitCompany && nameField && nameField !== explicitCompany ? nameField : null;

  const domainRaw = firstNonEmpty(
    hint.domain,
    data.domain,
    data.Domain,
    data.website,
    data.Website,
    data.website_url,
    data['company.domain'],
    data['company.website'],
    data['Company Domain'],
    data['Company Website'],
  );

  return {
    companyName: resolvedCompany,
    personName,
    domain: normalizeDomainish(domainRaw) || domainRaw || null,
    displayName: resolvedCompany,
  };
}

/** Short plain-language explanation for the review card. */
export function plainLanguageConflictExplanation(error: string): string {
  const cleaned = stripStorePrefix(error);
  if (/already has a different website/i.test(cleaned)) {
    return 'Nynch already has a company with this name, but a different website. We did not create a duplicate — your data is safe.';
  }
  if (/more than one company|ambiguous|similarly named/i.test(cleaned)) {
    return 'More than one company in Nynch could match this name. We paused so you can choose what to do.';
  }
  if (/Review the company|Review it before/i.test(cleaned)) {
    return 'This row needs a quick check before we create or link a company. Nothing was lost.';
  }
  return cleaned || 'This row needs a quick check. Nothing was duplicated.';
}

export function buildConflictRows(
  errors: string[],
  parsedData: Record<string, string>[],
): { conflicts: ImportConflictRow[]; otherErrors: string[] } {
  const conflicts: ImportConflictRow[] = [];
  const otherErrors: string[] = [];

  for (const line of errors) {
    const parsed = parseImportErrorLine(line);
    if (!parsed) {
      otherErrors.push(line);
      continue;
    }
    if (!isIdentityConflictError(parsed.message)) {
      otherErrors.push(line);
      continue;
    }
    const data = parsedData[parsed.row - 1] ?? {};
    const identity = extractConflictIdentity(data, parsed.row, parsed.message);
    // Drop the trailing [name · domain] hint from user-facing error text.
    const cleanError = stripStorePrefix(parsed.message).replace(/\s*\[[^\]]+\]\s*$/, '').trim();

    conflicts.push({
      row: parsed.row,
      name: identity.displayName,
      companyName: identity.companyName,
      personName: identity.personName,
      domain: identity.domain,
      error: cleanError,
      kind: 'identity_conflict',
    });
  }

  return { conflicts, otherErrors };
}

/** Normalize job.errors (array of objects or "Row N: …" strings) into line form. */
export function jobErrorsToLines(errors: unknown): string[] {
  if (!Array.isArray(errors)) return [];
  return errors.map((value, index) => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') {
      const row = value as { row?: unknown; error?: unknown; message?: unknown };
      const n = typeof row.row === 'number' ? row.row : index + 1;
      const msg =
        typeof row.error === 'string'
          ? row.error
          : typeof row.message === 'string'
            ? row.message
            : 'Import row failed';
      return `Row ${n}: ${msg}`;
    }
    return `Row ${index + 1}: Import row failed`;
  });
}

/**
 * "Row N: reason" for each row of the file that finally failed. The import
 * runner marks those entries `rerun: true`; company entries and stop notices
 * are not rows of the file.
 */
export function failedRowLines(errors: unknown): string[] {
  if (!Array.isArray(errors)) return [];
  return errors.flatMap((entry) => {
    const record = (entry ?? {}) as { row?: unknown; error?: unknown; rerun?: unknown };
    return record.rerun === true && typeof record.row === 'number'
      ? [`Row ${record.row}: ${typeof record.error === 'string' ? record.error : 'Import row failed'}`]
      : [];
  });
}

/**
 * Can "Retry failed rows" send anything? The same test the import runner
 * makes (`supabase/functions/_shared/csv-import-rerun.ts`): rows it recorded
 * as failed; companies of a people-and-companies import whose save call
 * failed; or, for a people-and-companies import from before it recorded
 * them, a whole batch of people written as one "Contact store:" entry. A
 * company the save refused goes through the company review instead.
 */
export function hasRetryableFailedRows(job: { entity_type: string; errors?: unknown; stats?: unknown }): boolean {
  if (failedRowLines(job.errors).length > 0) return true;
  const failedCompanies = (job.stats as { failed_company_rows?: unknown } | null | undefined)?.failed_company_rows;
  if (job.entity_type === 'people_companies' && Array.isArray(failedCompanies) && failedCompanies.length > 0) {
    return true;
  }
  return job.entity_type === 'people_companies' &&
    Array.isArray(job.errors) &&
    job.errors.some((entry) => {
      const record = (entry ?? {}) as { row?: unknown; error?: unknown; code?: unknown };
      return typeof record.row === 'number' &&
        record.code === undefined &&
        typeof record.error === 'string' &&
        record.error.startsWith('Contact store: ');
    });
}

function asStringRecord(row: unknown): Record<string, string> {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(row as Record<string, unknown>)) {
    if (v === null || v === undefined) continue;
    out[k] = typeof v === 'string' ? v : String(v);
  }
  return out;
}

/**
 * Build review rows from a stored csv_import_jobs row (errors + csv_data).
 * csv_data is 0-indexed mapped records; error.row is 1-based into that array.
 */
export function buildConflictsFromImportJob(job: {
  errors?: unknown;
  csv_data?: unknown;
}): { conflicts: ImportConflictRow[]; rows: Record<string, string>[]; otherErrors: string[] } {
  const rawRows = Array.isArray(job.csv_data) ? job.csv_data : [];
  const rows = rawRows.map(asStringRecord);
  const { conflicts, otherErrors } = buildConflictRows(jobErrorsToLines(job.errors), rows);
  return { conflicts, rows, otherErrors };
}
